import { Component, computed, input, output, signal, WritableSignal } from '@angular/core';

export function clampValue(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function nearestSnapPointIndex(value: number, snapPoints: readonly number[]): number {
  let closestIndex = 0;
  for (let index = 1; index < snapPoints.length; index++) {
    if (Math.abs(snapPoints[index] - value) < Math.abs(snapPoints[closestIndex] - value)) {
      closestIndex = index;
    }
  }

  return closestIndex;
}

/**
 * Position of `value` along [domainMin, domainMax] as a 0-100 percentage, proportional to the
 * real value difference — NOT to the index of a point in a snap-point list. This is what keeps
 * track distance between e.g. 5 and 8 visually larger than between 3 and 5.
 */
export function percentFor(value: number, domainMin: number, domainMax: number): number {
  if (domainMax === domainMin) {
    return 0;
  }

  return ((clampValue(value, domainMin, domainMax) - domainMin) / (domainMax - domainMin)) * 100;
}

export function snapToNearest(
  value: number,
  options: { min: number; max: number; step: number; snapPoints: readonly number[] | undefined },
): number {
  const { min, max, step, snapPoints } = options;

  if (!snapPoints || snapPoints.length === 0) {
    return step > 0 ? clampValue(Math.round((value - min) / step) * step + min, min, max) : clampValue(value, min, max);
  }

  return snapPoints[nearestSnapPointIndex(value, snapPoints)];
}

@Component({
  selector: 'rid-slider-input',
  imports: [],
  templateUrl: './rid-slider-input.html',
  host: {
    class: 'block w-full',
  },
})
export class RidSliderInput {
  label = input<string | undefined>(undefined);
  /** Accessible name for the slider and number input when no visible `label` is rendered. */
  ariaLabel = input<string | undefined>(undefined);
  unit = input<string | undefined>(undefined);
  min = input<number>(0);
  max = input.required<number>();
  step = input<number>(1);
  snapPoints = input<readonly number[] | undefined>(undefined);
  disabled = input<boolean>(false);
  valueSignal = input<WritableSignal<number> | undefined>(undefined);

  valueChange = output<number>();

  protected readonly labelId = `rid-slider-input-label-${Math.random().toString(36).slice(2)}`;
  protected readonly dragging = signal(false);

  private internalValue = signal<number | undefined>(undefined);
  value = computed(() => clampValue(this.valueSignal()?.() ?? this.internalValue() ?? this.min(), this.min(), this.max()));

  // With snap points, the track's draggable domain is exactly their own span (first..last point),
  // rather than the arbitrary min()/max(), so track distance between points is proportional to
  // their real value difference (5->8 further than 3->5) and the thumb always reaches both ends.
  protected readonly domainMin = computed(() => this.snapPoints()?.[0] ?? this.min());
  protected readonly domainMax = computed(() => {
    const snapPoints = this.snapPoints();
    return snapPoints?.length ? snapPoints[snapPoints.length - 1] : this.max();
  });

  protected readonly thumbPercent = computed(() => percentFor(this.value(), this.domainMin(), this.domainMax()));
  protected readonly markPercents = computed(() =>
    (this.snapPoints() ?? []).map((point) => percentFor(point, this.domainMin(), this.domainMax())),
  );
  protected readonly valueText = computed(() => {
    const unit = this.unit();
    return unit ? `${this.value()} ${unit}` : `${this.value()}`;
  });

  // Snap-point moves are discrete jumps between fixed stops, so animating position while dragging
  // reads as a nice slide-to-next-stop. A continuous (non-snap) drag updates every pixel of pointer
  // movement instead, where the same animation would just lag behind the pointer.
  protected readonly animatePositionWhileDragging = computed(() => (this.snapPoints()?.length ?? 0) > 0);

  protected onTrackPointerDown(event: PointerEvent) {
    if (this.disabled()) {
      return;
    }

    event.preventDefault();
    const element = event.currentTarget as HTMLElement;
    element.setPointerCapture(event.pointerId);
    this.dragging.set(true);

    const updateFromClientX = (clientX: number) => {
      const rect = element.getBoundingClientRect();
      const ratio = rect.width === 0 ? 0 : Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const domainMin = this.domainMin();
      const domainMax = this.domainMax();

      this.setValue(
        snapToNearest(domainMin + ratio * (domainMax - domainMin), {
          min: this.min(),
          max: this.max(),
          step: this.step(),
          snapPoints: this.snapPoints(),
        }),
      );
    };

    updateFromClientX(event.clientX);

    const onMove = (moveEvent: PointerEvent) => updateFromClientX(moveEvent.clientX);
    const onUp = () => {
      this.dragging.set(false);
      element.removeEventListener('pointermove', onMove);
      element.removeEventListener('pointerup', onUp);
      element.removeEventListener('pointercancel', onUp);
    };

    element.addEventListener('pointermove', onMove);
    element.addEventListener('pointerup', onUp);
    element.addEventListener('pointercancel', onUp);
  }

  protected onKeyDown(event: KeyboardEvent) {
    if (this.disabled()) {
      return;
    }

    const snapPoints = this.snapPoints();

    if (snapPoints?.length) {
      const currentIndex = nearestSnapPointIndex(this.value(), snapPoints);
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowUp':
        case 'PageUp':
          event.preventDefault();
          this.setValue(snapPoints[Math.min(snapPoints.length - 1, currentIndex + 1)]);
          break;
        case 'ArrowLeft':
        case 'ArrowDown':
        case 'PageDown':
          event.preventDefault();
          this.setValue(snapPoints[Math.max(0, currentIndex - 1)]);
          break;
        case 'Home':
          event.preventDefault();
          this.setValue(snapPoints[0]);
          break;
        case 'End':
          event.preventDefault();
          this.setValue(snapPoints[snapPoints.length - 1]);
          break;
      }
      return;
    }

    // PageUp/PageDown take a bigger jump than the arrow keys, per the ARIA slider pattern.
    const increment = event.key === 'PageUp' || event.key === 'PageDown' ? this.step() * 10 : this.step();

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
      case 'PageUp':
        event.preventDefault();
        this.setValue(clampValue(this.value() + increment, this.min(), this.max()));
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
      case 'PageDown':
        event.preventDefault();
        this.setValue(clampValue(this.value() - increment, this.min(), this.max()));
        break;
      case 'Home':
        event.preventDefault();
        this.setValue(this.min());
        break;
      case 'End':
        event.preventDefault();
        this.setValue(this.max());
        break;
    }
  }

  // Deliberately free-form: unlike the track/keyboard, typing a value here does not snap it to the
  // nearest snap point, so the field can be used for precise entry outside the slider's stops.
  protected onNumberInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const parsed = Number(target.value);

    if (target.value === '' || Number.isNaN(parsed)) {
      // Reject invalid/empty input by restoring the field to the current value, rather than
      // leaving it visually blank/stale while the underlying value is unchanged.
      target.value = String(this.value());
      return;
    }

    this.setValue(clampValue(parsed, this.min(), this.max()));
  }

  private setValue(value: number) {
    if (this.disabled() || value === this.value()) {
      return;
    }

    const valueSignal = this.valueSignal();
    if (valueSignal) {
      valueSignal.set(value);
    } else {
      this.internalValue.set(value);
    }

    this.valueChange.emit(value);
  }
}
